import React, { useCallback, useState, useEffect } from 'react'
import { useModal } from 'react-modal-hook'
import { Formik, Field, Form } from 'formik'
import AuthService from '../../../services/auth.service'
import SettingService from '../../../services/settings.service'
import { ChangePasswordSchema } from '../../../schemas/settings'
import InputError from '../../../components/InputError/InputError'
import Loading from 'custom/assets/loaders/Loading'
import NeoButton from '../../../components/NeoButton/NeoButton'
import { DIALOG_KEYS, SIDENAV_KEYS, VALIDATION_MESSAGES } from '../../../utils/translation-constants'
import Modal from '../../../components/Modal/Modal'
import { useTranslation } from 'react-i18next'
import LayoutWithOutAccountCard from '../../../layout/Layout/layout-without-card'
import { useHistory } from 'react-router-dom'
import { VALIDATION } from '../../../utils/translation-constants'
import * as Yup from 'yup'
import PasswordStrengthBar from 'react-password-strength-bar';

function ChangePassword() {
  const [loading, setLoading] = useState(false)
  const [criteria, setCriteria] = useState([])
  const [message, setMessage] = useState('')
  const { t } = useTranslation()
  const [passwordPattern, setPasswordPattern] = useState(null)
  const [passwordLength, setPasswordLength] = useState()
  const history = useHistory()

  const [showModal, hideModal] = useModal(
    () => (
      <Modal>
        <div className='modal-error-body'>
          <h6>
            <b>{message?.code}</b> : {message?.message}
          </h6>
          <div className='modal-btn-style'>
            <NeoButton
              mode='newDesign'
              type='button'
              text={t(DIALOG_KEYS.CLOSE)}
              onClick={() => {
                hideModal()
              }}
            />
          </div>
        </div>
      </Modal>
    ),
    [message],
  )

  const changePassword = useCallback(
    async (data, { setSubmitting, resetForm }) => {
      setLoading(true)
      setSubmitting(true)
      try {
        const response = await SettingService.changeUserPassword({
          oldPwd: data.oldPassword,
          newPwd: data.newPassword,
        })
        setMessage(response)
        showModal()
      } catch (err) {
        setLoading(false)
      }
      setSubmitting(false)
      setLoading(false)
      resetForm()
    },
    [showModal],
  )

  useEffect(() => {
    const getPwdCriteria = async () => {
      try {
        setLoading(true)
        const response = await AuthService.getPasswordCriteria()
        setCriteria(response.userPasswordCriteriaMap)
        // const pwdMaxLength = response['userPasswordCriteria'][3].value
        // const pwdMinLength = response['userPasswordCriteria'][4].value
        // const pwdMinAlphabetLength = response['userPasswordCriteria'][0].value
        // const pwdMinNumberLength = response['userPasswordCriteria'][1].value
        // const pwdMinSplCharLength = response['userPasswordCriteria'][2].value
        const pwdMaxLength = response['userPasswordCriteriaMap']['MAX_PWD_LENGTH']
        const pwdMinLength = response['userPasswordCriteriaMap']['MIN_PWD_LENGTH']
        const pwdMinAlphabetLength = response['userPasswordCriteriaMap']['ALPHABETS']
        const pwdMinNumberLength = response['userPasswordCriteriaMap']['NUMERICS']
        const pwdMinSplCharLength = response['userPasswordCriteriaMap']['SPECIAL_CHARACTERS']
        const pattern = `(?=^.{${pwdMinLength},${pwdMaxLength}}$)(?=(.*\\d){${pwdMinNumberLength}})(?=(.*[A-Za-z]){${pwdMinAlphabetLength}})(?=(.*[\\+\\- \\= \\[\\]\\{ \\}\\;\\-\\(\\)\\<\\>\\_'!@#$%^&*?\"|/,.:\`~\\\\]){${pwdMinSplCharLength}})(?!.*[\\s])^.*`
        setPasswordPattern(pattern)
        setPasswordLength(pwdMaxLength)
        setLoading(false)
      } catch (err) {
        setLoading(false)
      }
    }
    getPwdCriteria()
  }, [])

  function renderLoading() {
    return (
      <div className='mx-4 rounded d-flex justify-content-center align-items-center'>
        <Loading width='50px' height='50px' />
      </div>
    )
  }

  function handleKeyDown(e) {
    if (e.key === ' ') {
      e.preventDefault()
    }
  }

  const ChangePasswordSchema = Yup.object().shape({
    newPassword: Yup.string()
      .required(t(VALIDATION.NEWPASSWORD))
      .matches(new RegExp(passwordPattern), t(VALIDATION_MESSAGES.PASSWORD_CRITERIA)),
    confirmNewPassword: Yup.string()
      .oneOf([Yup.ref('newPassword'), null], t(VALIDATION.PASSWORDNOTMATCH))
      .required(t(VALIDATION.CONFIRMNEWPASSWORD)),
    oldPassword: Yup.string().required(t(VALIDATION.OLD_PASSWORD)),
  })

  return (
    <LayoutWithOutAccountCard
      breadCumKey='CHANGE_PASSWORD'
      keyPath={'CHANGE_PASSWORD'}
      showEmptySpace={true}>
      <div>
        <Formik
          initialValues={{
            oldPassword: '',
            newPassword: '',
            confirmNewPassword: '',
          }}
          validationSchema={ChangePasswordSchema}
          onSubmit={changePassword}>
          {({ isSubmitting, errors, touched, resetForm , values}) => (
            <Form className='new-design-form-style'>
              <div className='heading-style'>{t(SIDENAV_KEYS.SETTINGS_KEYS.CHANGE_PASSWORD)}</div>
              <div className='d-flex row'>
                <div className='col-12'>
                  <div className='form-group my-2'>
                    <label className='control-label'>
                      {t(SIDENAV_KEYS.SETTINGS_KEYS.CHANGE_PASSWORD_KEYS.OLD_PASSWORD)}
                    </label>
                    <Field tabIndex="1" as='input' name='oldPassword' className='form-control' type='password' />
                    <InputError text={errors.oldPassword} touched={touched.oldPassword} />
                  </div>
                  <div className='form-group my-2'>
                    <label className='control-label'>
                      {t(SIDENAV_KEYS.SETTINGS_KEYS.CHANGE_PASSWORD_KEYS.NEW_PASSWORD)}
                    </label>
                    <Field
                      tabIndex="2"
                      as='input'
                      name='newPassword'
                      className='form-control'
                      type='password'
                      onKeyDown={handleKeyDown}
                      maxLength={passwordLength}
                    />
                    <InputError text={errors.newPassword} touched={touched.newPassword} />
                    <div className='pt-2'>
                    <PasswordStrengthBar password={values.newPassword} />

                    </div>
                  </div>
                  <div className='form-group my-2'>
                    <label className='control-label'>
                      {t(SIDENAV_KEYS.SETTINGS_KEYS.CHANGE_PASSWORD_KEYS.CONFIRM_NEW_PASSWORD)}
                    </label>
                    <Field
                      tabIndex="3"
                      as='input'
                      name='confirmNewPassword'
                      className='form-control'
                      type='password'
                      maxLength={passwordLength}
                    />
                    <InputError
                      text={errors.confirmNewPassword}
                      touched={touched.confirmNewPassword}
                      type='password'
                    />
                  </div>
                  

                  <div className='mt-4 ml-auto px-0'>
                    {isSubmitting ? (
                      renderLoading()
                    ) : (
                      <NeoButton
                      tabIndex="4"
                        type='submit'
                        disabled={isSubmitting}
                        text={t(SIDENAV_KEYS.SETTINGS_KEYS.CHANGE_PASSWORD_KEYS.SUBMIT)}
                        mode='newDesign'
                      />
                    )}
                  </div>

                </div>
                <div className='col-12  mt-4'>
                  <div className='card passwordCriteria'>
                    <div className='card-body'>
                      <h6 className='card-title row mx-3'>
                        {t(SIDENAV_KEYS.SETTINGS_KEYS.CHANGE_PASSWORD_KEYS.SUBTITLE)}
                      </h6>
                      {/* {criteria.map((value, i) => (
                        <p className='card-text col' key={i}>
                          {value.description} : <b>{value.value}</b>
                        </p>
                      ))} */}
                      {Object.keys(criteria).map((key, i) => (
                        <p key={i} className='card-text col'>
                          {t(key)} : <b>{criteria[key]}</b>
                        </p>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </LayoutWithOutAccountCard>
  )
}

export default ChangePassword
